import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { hydrateLearned, isPhoneticLang, onLearnedChange, transliterateOnWordEnd } from './index';

const KEY = 'phoneticInput';
const LEARNED_KEY = 'phoneticLearned';
let enabled = false;

// Words the user picked from suggestions (best-effort persistence).
AsyncStorage.getItem(LEARNED_KEY)
  .then((v) => v && hydrateLearned(JSON.parse(v)))
  .catch(() => {});
onLearnedChange((d) => {
  AsyncStorage.setItem(LEARNED_KEY, JSON.stringify(d)).catch(() => {});
});

const listeners = new Set<() => void>();

AsyncStorage.getItem(KEY)
  .then((v) => {
    enabled = v === '1';
    listeners.forEach((l) => l());
  })
  .catch(() => {});

export const setPhoneticInput = (on: boolean): void => {
  enabled = on;
  AsyncStorage.setItem(KEY, on ? '1' : '0').catch(() => {});
  listeners.forEach((l) => l());
};

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};
const read = () => enabled;

/** Active phonetic language ('ta' | 'ml') or null when off/unsupported. */
export function usePhoneticLang(): string | null {
  const { i18n } = useTranslation();
  const on = useSyncExternalStore(subscribe, read, read);
  const lang = (i18n.language || 'en').slice(0, 2);
  return on && isPhoneticLang(lang) ? lang : null;
}

export function usePhoneticEnabled(): [boolean, (on: boolean) => void] {
  const on = useSyncExternalStore(subscribe, read, read);
  return [on, setPhoneticInput];
}

/**
 * For raw (non-wrapper) free-text inputs:
 *   const ph = usePhoneticText();
 *   <TextInput value={v} onChangeText={(t) => setV(ph.onChange(t))} onBlur={() => setV(ph.onBlur(v))} />
 */
export function usePhoneticText() {
  const lang = usePhoneticLang();
  return {
    onChange: (text: string) => (lang ? transliterateOnWordEnd(text, lang) : text),
    onBlur: (text: string) => (lang ? transliterateOnWordEnd(text, lang, true) : text),
  };
}
